import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  MessageCircle,
  ShieldAlert,
  Search,
  ArrowLeft,
  AlertTriangle,
  Loader2,
  Users,
  Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = "/api";

export default function AdminMessageMonitor() {
  const { getAuthHeader } = useAuth();
  const headers = useMemo(() => ({ headers: getAuthHeader() }), [getAuthHeader]);

  const [view, setView] = useState("tutors"); // tutors | conversations | messages
  const [tutors, setTutors] = useState([]);
  const [activeTutor, setActiveTutor] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // 1) Load tutors overview on mount
  const loadTutors = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/messages/admin/tutors-overview`, headers);
      setTutors(r.data.tutors || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tutors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTutors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Open a tutor → list of conversations
  const openTutor = async (tutor) => {
    setActiveTutor(tutor);
    setView("conversations");
    setLoading(true);
    setConversations([]);
    try {
      const r = await axios.get(
        `${API}/messages/admin/tutor/${tutor.tutor_id}/conversations`,
        headers
      );
      setConversations(r.data.conversations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  // 3) Open a conversation → read-only message thread
  const openConversation = async (conv) => {
    setActiveConv(conv);
    setView("messages");
    setLoading(true);
    setMessages([]);
    try {
      const r = await axios.get(
        `${API}/messages/admin/conversation/${conv.id}/messages`,
        headers
      );
      setMessages(r.data.messages || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const filteredTutors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tutors;
    return tutors.filter(
      (t) =>
        t.tutor_name?.toLowerCase().includes(q) ||
        t.email?.toLowerCase().includes(q)
    );
  }, [tutors, search]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
          {view !== "tutors" && (
            <button
              onClick={() => {
                if (view === "messages") {
                  setView("conversations");
                  setActiveConv(null);
                  setMessages([]);
                } else {
                  setView("tutors");
                  setActiveTutor(null);
                  setConversations([]);
                }
              }}
              className="flex items-center gap-1 text-blue-700 hover:text-blue-900"
              data-testid="admin-msg-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
        </div>
        <h1
          className="text-3xl font-bold text-slate-900"
          data-testid="admin-message-monitor-title"
        >
          {view === "tutors" && "Message Monitoring"}
          {view === "conversations" && `${activeTutor?.tutor_name || ""}'s conversations`}
          {view === "messages" &&
            `${activeTutor?.tutor_name || ""} ↔ ${activeConv?.student_name || ""}`}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {view === "tutors" &&
            "Read-only view of all teacher-student communication. Pick a teacher to inspect their conversations."}
          {view === "conversations" &&
            "All conversations this teacher has had with students."}
          {view === "messages" && (
            <span className="inline-flex items-center gap-1 text-orange-700">
              <Eye className="w-3.5 h-3.5" /> Read-only audit view — admin cannot reply here.
            </span>
          )}
        </p>
      </div>

      {/* Safety reminder banner (only on landing) */}
      {view === "tutors" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
          <span>
            Communication is restricted to <strong>preset messages only</strong>.
            Blocked attempts (phone numbers, emails, external platforms) are still
            logged here for your review.
          </span>
        </div>
      )}

      {/* TUTORS VIEW */}
      {view === "tutors" && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search tutors by name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="admin-msg-search"
                />
              </div>
            </div>
            {loading ? (
              <div className="py-16 flex items-center justify-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : filteredTutors.length === 0 ? (
              <div className="text-center py-12 text-sm text-slate-500">
                No tutors found.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredTutors.map((t) => (
                  <button
                    key={t.tutor_id}
                    onClick={() => openTutor(t)}
                    className="w-full text-left px-5 py-4 hover:bg-blue-50 transition-colors flex items-center gap-4"
                    data-testid={`admin-msg-tutor-${t.tutor_id}`}
                  >
                    <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-base flex-shrink-0">
                      {t.tutor_name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">
                        {t.tutor_name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {t.email}
                      </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-6 text-xs">
                      <div className="text-center">
                        <p className="font-semibold text-slate-900">
                          {t.conversation_count}
                        </p>
                        <p className="text-slate-500">chats</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-slate-900">
                          {t.messages_sent}
                        </p>
                        <p className="text-slate-500">sent</p>
                      </div>
                      {t.blocked_attempts > 0 && (
                        <div
                          className="text-center text-red-600"
                          data-testid={`admin-msg-blocked-${t.tutor_id}`}
                        >
                          <p className="font-semibold flex items-center gap-1 justify-center">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            {t.blocked_attempts}
                          </p>
                          <p>blocked</p>
                        </div>
                      )}
                      <div className="text-right min-w-[100px]">
                        <p className="text-slate-700">
                          {formatDate(t.last_message_at)}
                        </p>
                        <p className="text-slate-400">last activity</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CONVERSATIONS VIEW */}
      {view === "conversations" && (
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-16 flex items-center justify-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-12 text-sm text-slate-500">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                No conversations yet for this teacher.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {conversations.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => openConversation(c)}
                    className="w-full text-left px-5 py-4 hover:bg-blue-50 transition-colors flex items-center gap-3"
                    data-testid={`admin-msg-conv-${c.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-semibold flex-shrink-0">
                      {c.student_name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">
                        {c.student_name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {c.last_message_preview || "No messages yet"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-slate-500">
                      <span>
                        <strong className="text-slate-900">{c.message_count || 0}</strong> msgs
                      </span>
                      {(c.blocked_count || 0) > 0 && (
                        <span className="text-red-600 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {c.blocked_count} blocked
                        </span>
                      )}
                      <span className="hidden sm:inline text-slate-400">
                        {formatDate(c.last_message_at)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* MESSAGES (READ-ONLY) */}
      {view === "messages" && (
        <Card>
          <CardContent className="p-0">
            <div className="px-5 py-3 bg-orange-50 border-b border-orange-200 text-xs text-orange-900 flex items-start gap-2">
              <Eye className="w-4 h-4 mt-0.5 flex-shrink-0 text-orange-700" />
              <span>
                <strong>Read-only audit view.</strong> Messages cannot be sent or edited from
                here. Blocked attempts are highlighted in red.
              </span>
            </div>
            {loading ? (
              <div className="py-16 flex items-center justify-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-12 text-sm text-slate-500">
                No messages exchanged yet.
              </div>
            ) : (
              <div
                className="p-4 space-y-3 max-h-[600px] overflow-y-auto"
                data-testid="admin-msg-thread"
              >
                {messages.map((m) => {
                  const isTutor = m.sender_role === "tutor";
                  const isBlocked = m.blocked === true;
                  return (
                    <div
                      key={m.id}
                      className={cn(
                        "flex",
                        isTutor ? "justify-end" : "justify-start"
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[75%] rounded-2xl px-4 py-2 text-sm border",
                          isBlocked
                            ? "bg-red-50 border-red-300 text-red-900"
                            : isTutor
                              ? "bg-blue-600 text-white border-blue-600 rounded-br-sm"
                              : "bg-slate-100 text-slate-900 border-slate-200 rounded-bl-sm"
                        )}
                        data-testid={`admin-msg-${m.id}`}
                      >
                        <p className="text-[10px] uppercase tracking-wider opacity-80 mb-1">
                          {m.sender_name} · {isTutor ? "Tutor" : "Student"}
                          {isBlocked && (
                            <span className="ml-2 inline-flex items-center gap-1 text-red-700 font-bold">
                              <AlertTriangle className="w-3 h-3" />
                              BLOCKED ({m.blocked_reason || "violation"})
                            </span>
                          )}
                        </p>
                        <p className="whitespace-pre-wrap break-words">{m.content}</p>
                        <p
                          className={cn(
                            "text-[10px] mt-1 opacity-70",
                            isTutor && !isBlocked ? "text-white" : "text-slate-500"
                          )}
                        >
                          {formatDate(m.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 text-center">
              <Button
                variant="outline"
                onClick={() => {
                  setView("conversations");
                  setActiveConv(null);
                  setMessages([]);
                }}
                data-testid="admin-msg-back-to-convs"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to conversations
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
